import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation } from '../lib/wizardSteps';
import { WIZARD_EXIT_ROUTE, WIZARD_PATHS, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { ArrowRight, SkipForward, Lightbulb } from 'lucide-react';

interface DirectionHint {
  label: string;
  description: string;
}

export function ArticleDirection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, currentIdea, loadSessionById } = useSession();
  const [direction, setDirection] = useState('');
  const [focus, setFocus] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardIdea, setDashboardIdea] = useState<any>(null);
  const [hints, setHints] = useState<DirectionHint[]>([]);
  const [hintsLoading, setHintsLoading] = useState(false);
  const [selectedHintIndex, setSelectedHintIndex] = useState<number | null>(null);
  // Aantal ideeën (aangeboden + gekozen) van deze sessie: 2 of meer = AI-ideeënlijst.
  const [ideaCount, setIdeaCount] = useState(0);

  const sessionId = sessionIdFromLocation(location);

  useEffect(() => {
    if (sessionId) {
      loadData(sessionId);
    } else {
      setDataLoading(false);
    }
  }, [sessionId]);

  // Invalshoek-suggesties alleen zolang het artikel nog niet geschreven is.
  useEffect(() => {
    const idea = dashboardIdea || currentIdea;
    const written = !!(dashboardSession || session)?.article_content;
    if (idea?.idea_title && !written) {
      loadHints(idea);
    }
  }, [dashboardIdea, currentIdea, dashboardSession, session]);

  const loadData = async (sessionId: string) => {
    setDataLoading(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError || !sessionData) {
        setDataLoading(false);
        return;
      }

      setDashboardSession(sessionData);
      setDirection(sessionData.article_direction ?? '');
      setFocus(sessionData.article_focus ?? '');

      const { count, error: countError } = await supabase
        .from('blog_ideas')
        .select('id', { count: 'exact', head: true })
        .eq('session_id', sessionId)
        .in('status', ['presented', 'approved']);
      if (countError) console.error('Kon ideeën niet tellen:', countError);
      setIdeaCount(count ?? 0);

      if (sessionData.approved_idea_id) {
        const { data: ideaData } = await supabase
          .from('blog_ideas')
          .select('*')
          .eq('id', sessionData.approved_idea_id)
          .maybeSingle();

        if (ideaData) setDashboardIdea(ideaData);
      }

      setDataLoading(false);
    } catch {
      setDataLoading(false);
    }
  };

  const loadHints = async (idea: any) => {
    setHintsLoading(true);
    try {
      const activeSession = dashboardSession || session;
      const userId = activeSession?.user_id;
      if (!userId) {
        setHintsLoading(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY);

      const response = await fetch(`${supabaseUrl}/functions/v1/openai-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          promptType: 'article_direction_hints',
          user_id: userId,
          data: {
            idea_title: idea.idea_title,
            idea_content: idea.idea_content || '',
          },
        }),
      });

      if (!response.ok) {
        setHintsLoading(false);
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        try {
          const parsed = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
          if (parsed.hints && Array.isArray(parsed.hints)) {
            setHints(parsed.hints.slice(0, 3));
          }
        } catch {
          // stille fallback
        }
      }
    } catch {
      // stille fallback
    } finally {
      setHintsLoading(false);
    }
  };

  const handleHintClick = (hint: DirectionHint, index: number) => {
    setSelectedHintIndex(index);
    setDirection(hint.description);
  };

  const activeSession = dashboardSession || session;
  const activeIdea = dashboardIdea || currentIdea;
  const { handleStepClick } = useWizardNavigation(activeSession);
  const articleWritten = !!activeSession?.article_content;
  // Terug: naar de AI-ideeënlijst van deze sessie, of naar het ideeënoverzicht
  // als de sessie uit één bewaard idee is gestart.
  const ideaListPath = ideaCount >= 2 ? WIZARD_PATHS.ideaSelect : '/ideeen';
  const goBack = () => navigate(wizardUrl(ideaListPath, activeSession?.id));
  const goToArticle = () => navigate(wizardUrl(WIZARD_PATHS.articleEdit, activeSession?.id));

  const proceed = async (withDirection: boolean) => {
    if (!activeSession) return;
    setLoading(true);

    try {
      const updates: Record<string, string | null> = {
        article_direction: withDirection && direction.trim() ? direction.trim() : null,
        article_focus: withDirection && focus.trim() ? focus.trim() : null,
      };

      await supabase
        .from('sessions')
        .update(updates)
        .eq('id', activeSession.id);

      navigate(wizardUrl(WIZARD_PATHS.articleGenerate, activeSession.id));
    } catch {
      setLoading(false);
    }
  };

  const steps = getWizardSteps(activeSession, 2, { ideaStepPath: ideaListPath });

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Invalshoek"
          onBack={goBack}
          onClose={() => navigate(WIZARD_EXIT_ROUTE)}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  if (!activeSession) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Invalshoek"
          onBack={goBack}
          onClose={() => navigate(WIZARD_EXIT_ROUTE)}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <h2 className="text-xl font-bold text-red-800 mb-2">Geen sessie gevonden</h2>
            <p className="text-red-700 mb-4">Er is geen actieve sessie beschikbaar.</p>
            <button onClick={() => navigate(WIZARD_EXIT_ROUTE)} className="btn-secondary">
              Naar Mijn Blogs
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Invalshoek"
        onBack={goBack}
        onClose={() => navigate(WIZARD_EXIT_ROUTE)}
        hideSaveStatus
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />

        <div className="space-y-6 mt-6">
          {activeIdea && (
            <div className="card border border-gray-200 bg-white">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Goedgekeurd idee</p>
              <h2 className="text-lg font-bold text-[#231F20]">{activeIdea.idea_title}</h2>
              {activeIdea.idea_content && (
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">{activeIdea.idea_content}</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              {articleWritten ? 'Gekozen invalshoek' : 'Wil je de schrijver een richting meegeven?'}
            </h1>
            <p className="text-gray-600">
              {articleWritten
                ? 'Dit gaf je mee toen het artikel geschreven werd.'
                : 'Optioneel: geef een invalshoek of extra focus mee. De AI pakt dit mee bij het schrijven. Je kunt dit ook overslaan.'}
            </p>
          </div>

          {articleWritten && (
            <div className="card border border-green-200 bg-green-50">
              <p className="font-semibold text-[#231F20] mb-1">Het artikel is al geschreven met deze invalshoek.</p>
              <p className="text-sm text-gray-600">
                Wil je iets anders? Pas het artikel aan in de editor met 'Laat AI het aanpassen'. Een andere invalshoek betekent een nieuwe blog.
              </p>
            </div>
          )}

          {!articleWritten && (hintsLoading || hints.length > 0) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-[#FF6B35]" />
                <p className="text-sm font-semibold text-[#231F20]">
                  Kies een invalshoek, of schrijf zelf hieronder
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {hintsLoading
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <div
                        key={i}
                        className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse"
                      >
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                        <div className="h-3 bg-gray-100 rounded w-full mb-1" />
                        <div className="h-3 bg-gray-100 rounded w-5/6" />
                      </div>
                    ))
                  : hints.map((hint, index) => (
                      <button
                        key={index}
                        onClick={() => handleHintClick(hint, index)}
                        className={`text-left bg-white border rounded-xl p-4 transition-all duration-150 hover:shadow-md hover:border-[#FF6B35] focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:ring-offset-1 ${
                          selectedHintIndex === index
                            ? 'border-[#FF6B35] ring-2 ring-[#FF6B35] ring-offset-1 shadow-md'
                            : 'border-gray-200'
                        }`}
                      >
                        <p className="text-sm font-semibold text-[#231F20] mb-1">{hint.label}</p>
                        <p className="text-xs text-gray-500 leading-relaxed">{hint.description}</p>
                      </button>
                    ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-[#231F20]">
                Invalshoek
                <span className="ml-1 font-normal text-gray-500">(optioneel)</span>
              </label>
              <textarea
                className="textarea-field min-h-[90px]"
                readOnly={articleWritten}
                aria-readonly={articleWritten || undefined}
                placeholder={articleWritten ? 'Geen invalshoek meegegeven' : 'Bijv: schrijf vanuit het perspectief van een starter die nog geen ervaring heeft...'}
                value={direction}
                onChange={(e) => {
                  setDirection(e.target.value);
                  if (selectedHintIndex !== null) setSelectedHintIndex(null);
                }}
                maxLength={400}
              />
              {direction.length > 0 && (
                <p className="text-xs text-gray-400 text-right">{direction.length}/400</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-semibold text-[#231F20]">
                Extra focus
                <span className="ml-1 font-normal text-gray-500">(optioneel)</span>
              </label>
              <textarea
                className="textarea-field min-h-[90px]"
                readOnly={articleWritten}
                aria-readonly={articleWritten || undefined}
                placeholder={articleWritten ? 'Geen extra focus meegegeven' : 'Bijv: leg extra nadruk op de praktische toepasbaarheid en geef concrete voorbeelden...'}
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                maxLength={400}
              />
              {focus.length > 0 && (
                <p className="text-xs text-gray-400 text-right">{focus.length}/400</p>
              )}
            </div>
          </div>

          {articleWritten ? (
            <button
              onClick={goToArticle}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-5 h-5" />
              Naar het artikel
            </button>
          ) : (
          <div className="space-y-3">
            <button
              onClick={() => proceed(true)}
              disabled={loading || (!direction.trim() && !focus.trim())}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowRight className="w-5 h-5" />
              {loading ? 'Bezig...' : 'Schrijf artikel met deze richting'}
            </button>

            <button
              onClick={() => proceed(false)}
              disabled={loading}
              className="w-full text-gray-600 hover:text-[#231F20] py-3 flex items-center justify-center gap-2 font-medium transition-colors"
            >
              <SkipForward className="w-4 h-4" />
              Overslaan – laat de AI zelf kiezen
            </button>
          </div>
          )}
        </div>
      </main>
    </div>
  );
}
